import type { MaybeRefOrGetter } from "vue";

import { useHead, useSeoMeta } from "#app";
import { computed, toValue } from "vue";

import type { SEOPro } from "@solvari/statamic";

import { localeToIso } from "@solvari/translations";

import {
  useAbsoluteUrl,
  useCurrentUrl,
  useHostLocale,
} from "~/shared/composables/useHost.ts";
import { fallbackOgImage } from "~/shared/composables/useMetaData.ts";

type OgType = "article" | "website";

// Unknown, not SEOPro["robots"]: the API passes a stored `false` through.
function directiveName(directive: unknown): string | undefined {
  const name =
    typeof directive === "object" && directive !== null && "key" in directive
      ? directive.key
      : directive;

  return typeof name === "string" && name ? name : undefined;
}

function robotsToString(
  robots: readonly unknown[] | undefined,
  noindex: boolean,
): string | undefined {
  const directives = (robots ?? []).flatMap(
    (directive) => directiveName(directive) ?? [],
  );
  const effective = noindex
    ? [
        "noindex",
        ...directives.filter(
          (directive) => directive !== "index" && directive !== "noindex",
        ),
      ]
    : directives;

  return effective.length ? effective.join(", ") : undefined;
}

function useCmsSeo(
  seoProData: MaybeRefOrGetter<SEOPro | undefined>,
  options?: {
    articlePublishedTime?: MaybeRefOrGetter<string | null | undefined>;
    /** Overrides the CMS robots: noindex whatever the editor set. */
    noindex?: MaybeRefOrGetter<boolean>;
    ogType?: OgType;
    variantSourceUrl?: MaybeRefOrGetter<string | null | undefined>;
  },
) {
  const currentUrl = useCurrentUrl();
  const absoluteUrl = useAbsoluteUrl();
  const hostLocale = useHostLocale();

  const ogImageUrl = computed(
    () => toValue(seoProData)?.image?.url ?? fallbackOgImage,
  );

  useSeoMeta({
    title: () => toValue(seoProData)?.compiledTitle ?? undefined,
    description: () => toValue(seoProData)?.description ?? undefined,
    ogType: options?.ogType ?? "website",
    ogTitle: () =>
      toValue(seoProData)?.ogTitle ??
      toValue(seoProData)?.compiledTitle ??
      undefined,
    ogDescription: () => toValue(seoProData)?.description ?? undefined,
    ogSiteName: () => toValue(seoProData)?.siteName ?? undefined,
    ogUrl: currentUrl,
    ogImage: ogImageUrl,
    ogImageAlt: () => toValue(seoProData)?.image?.alt ?? undefined,
    robots: () =>
      robotsToString(
        toValue(seoProData)?.robots,
        toValue(options?.noindex) ?? false,
      ),
    twitterCard: () => {
      const card = toValue(seoProData)?.twitterCard;
      if (card === "summary_large_image" || card === "summary") {
        return card;
      }
      return "summary_large_image";
    },
    twitterTitle: () =>
      toValue(seoProData)?.ogTitle ??
      toValue(seoProData)?.compiledTitle ??
      undefined,
    twitterDescription: () => toValue(seoProData)?.description ?? undefined,
    twitterImage: ogImageUrl,
    articleModifiedTime: () =>
      options?.ogType === "article"
        ? (toValue(seoProData)?.lastModified ?? undefined)
        : undefined,
    articlePublishedTime: () =>
      options?.ogType === "article"
        ? (toValue(options.articlePublishedTime) ?? undefined)
        : undefined,
  });

  useHead(() => {
    // A split-URL variant canonicalises to its source, so the test does not split ranking signals.
    const variantSourceUrl = toValue(options?.variantSourceUrl);
    const canonical = variantSourceUrl
      ? absoluteUrl(variantSourceUrl)
      : currentUrl();
    const alternates = toValue(seoProData)?.alternateLocales;

    const link: (
      | { href: string; hreflang: string; rel: "alternate" }
      | { href: string; key: string; rel: "canonical" }
    )[] = [];

    if (canonical) {
      link.push({ rel: "canonical", href: canonical, key: "canonical" });
    }

    const alternateList = Object.values(alternates ?? {});

    if (canonical && alternateList.length) {
      link.push({
        rel: "alternate",
        hreflang: localeToIso(hostLocale),
        href: canonical,
      });
    }

    alternateList.forEach((alt) => {
      link.push({
        rel: "alternate",
        hreflang: alt.site.locale.replace("_", "-"),
        href: alt.url,
      });
    });

    return link.length ? { link } : {};
  });
}

export type { OgType };
export { useCmsSeo };
